import type {
  CyanOwnerAuthorityService,
  CyanDurableOwnerAuthorityResult,
} from "./cyanOwnerAuthorityService";

import {
  goalRiskToCyanOwnerRisk,
  type CyanGoalLikeRisk,
} from "./cyanOperationalApproval";

export interface DurableCyanOperationalApprovalInput {
  action: string;
  scope: string;

  risk: CyanGoalLikeRisk;

  requiresApproval: boolean;
  reversible: boolean;
  governedScope: boolean;

  destroysSs?: boolean;
  destroysExternalProject?: boolean;
  destroysCriticalProductionData?: boolean;
  revokesOwnerAccess?: boolean;
  exposesSecrets?: boolean;
  crossesUnknownAuthorityBoundary?: boolean;
}

export interface DurableCyanOperationalApprovalResult {
  allowed: boolean;
  requiresHumanOwner: boolean;

  approvalSatisfiedBy:
    | "NOT_REQUIRED"
    | "CYAN_OPERATIONAL_OWNER"
    | "DURABLE_CYAN_OPERATIONAL_OWNER"
    | "HUMAN_BREAK_GLASS"
    | "DENIED";

  reason: string;

  durableAuthority?: CyanDurableOwnerAuthorityResult;
}

export async function evaluateDurableCyanOperationalApproval(
  authority: CyanOwnerAuthorityService,
  input: DurableCyanOperationalApprovalInput,
): Promise<DurableCyanOperationalApprovalResult> {
  if (!input.requiresApproval) {
    return {
      allowed: true,
      requiresHumanOwner: false,
      approvalSatisfiedBy: "NOT_REQUIRED",
      reason: "Operation does not require elevated owner authority.",
    };
  }

  const durable = await authority.evaluate({
    action: input.action,

    scope: input.scope,

    risk: goalRiskToCyanOwnerRisk(input.risk),

    reversible: input.reversible,

    governedScope: input.governedScope,

    destroysSs: input.destroysSs,

    destroysExternalProject: input.destroysExternalProject,

    destroysCriticalProductionData: input.destroysCriticalProductionData,

    revokesOwnerAccess: input.revokesOwnerAccess,

    exposesSecrets: input.exposesSecrets,

    crossesUnknownAuthorityBoundary: input.crossesUnknownAuthorityBoundary,
  });

  if (durable.authorized) {
    return {
      allowed: true,

      requiresHumanOwner: false,

      approvalSatisfiedBy:
        durable.source === "DURABLE_AUTHORITY_REUSE"
          ? "DURABLE_CYAN_OPERATIONAL_OWNER"
          : "CYAN_OPERATIONAL_OWNER",

      reason: durable.reason,

      durableAuthority: durable,
    };
  }

  if (durable.requiresHumanOwner) {
    return {
      allowed: false,

      requiresHumanOwner: true,

      approvalSatisfiedBy: "HUMAN_BREAK_GLASS",

      reason: durable.reason,

      durableAuthority: durable,
    };
  }

  return {
    allowed: false,

    requiresHumanOwner: false,

    approvalSatisfiedBy: "DENIED",

    reason: durable.reason,

    durableAuthority: durable,
  };
}
